import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  signal
} from '@angular/core';

@Component({
  selector: 'app-preloader',
  standalone: true,
  templateUrl: './preloader.html',
  styleUrl: './preloader.scss'
})
export class Preloader implements OnInit, OnDestroy {
  @Output() finished = new EventEmitter<void>();

  isReady = signal(false);
  isHiding = signal(false);

  readonly testLoaderDurationMs = 5000;
  readonly minLoaderDurationMs = 2800;
  readonly maxLoaderDurationMs = 7000;
  readonly logoFillDurationMs = 2400;
  readonly fadeDurationMs = 700;
  private readonly mobileBreakpoint = 768;

  private readonly criticalImageUrls = [
    '/preloader-logo.svg',
    '/projects/1.png',
    '/projects/2.png',
    '/projects/3.png'
  ];

  private timerIds: number[] = [];
  private isDestroyed = false;
  private isFinished = false;

  ngOnInit(): void {
    void this.startPreloader();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;

    this.timerIds.forEach(timerId => {
      window.clearTimeout(timerId);
    });

    this.timerIds = [];
  }

  private get isMobile(): boolean {
    return window.innerWidth <= this.mobileBreakpoint;
  }

  continueToSite(): void {
    if (!this.isReady() || this.isFinished) {
      return;
    }

    this.unlockSound();
    this.hidePreloader();
  }

  private async startPreloader(): Promise<void> {
    const minDurationPromise = this.wait(this.minLoaderDurationMs);
    const assetsReadyPromise = this.waitForCriticalAssets();
    const maxDurationPromise = this.wait(this.maxLoaderDurationMs);

    await Promise.all([
      minDurationPromise,
      Promise.race([
        assetsReadyPromise,
        maxDurationPromise
      ])
    ]);

    this.showContinueButton();
  }

  private showContinueButton(): void {
    if (this.isDestroyed || this.isFinished) {
      return;
    }

    if (this.isMobile) {
      this.hidePreloader();
      return;
    }

    this.isReady.set(true);
  }

  private hidePreloader(): void {
    if (this.isDestroyed || this.isFinished) {
      return;
    }

    this.isFinished = true;
    this.isHiding.set(true);

    void this.wait(this.fadeDurationMs).then(() => {
      if (!this.isDestroyed) {
        this.finished.emit();
      }
    });
  }

  private unlockSound(): void {
    const audio = new Audio('/audio/sound.mp3');

    audio.volume = 0;
    audio.muted = true;

    const playPromise = audio.play();

    if (!playPromise) {
      return;
    }

    void playPromise
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
      })
      .catch(() => {});
  }

  private async waitForCriticalAssets(): Promise<void> {
    await Promise.allSettled([
      this.waitForFonts(),
      this.waitForImages()
    ]);
  }

  private async waitForFonts(): Promise<void> {
    if (!('fonts' in document)) {
      return;
    }

    try {
      await Promise.allSettled([
        document.fonts.load('650 28px "SF Pro"'),
        document.fonts.load('650 28px "SF Pro Display"'),
        document.fonts.ready
      ]);
    } catch {}
  }

  private async waitForImages(): Promise<void> {
    await Promise.allSettled(
      this.criticalImageUrls.map(url => this.loadImage(url))
    );
  }

  private loadImage(url: string): Promise<void> {
    return new Promise(resolve => {
      const image = new Image();

      const finish = () => {
        resolve();
      };

      image.onload = finish;
      image.onerror = finish;

      image.src = url;

      if (image.complete) {
        finish();
      }
    });
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => {
      const timerId = window.setTimeout(() => {
        resolve();
      }, ms);

      this.timerIds.push(timerId);
    });
  }
}
